import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom"; // Changed to react-router-dom
import { useTranslation } from 'react-i18next';
import { useSidebar } from "../context/SidebarContext";
import { ThemeToggleButton } from "../components/common/ThemeToggleButton";
import NotificationDropdown from "../components/header/NotificationDropdown";
import UserDropdown from "../components/header/UserDropdown";
import CommandSearch from "../components/header/CommandSearch";
import EmailVerificationChip from "../components/header/EmailVerificationChip";
import { useAuth } from "../context/AuthContext"; // Import useAuth
import { useCommandSearch } from "../hooks/useCommandSearch";

const AppHeader: React.FC = () => {
  const { t } = useTranslation();

  const { isMobileOpen, isExpanded, toggleSidebar, toggleMobileSidebar } = useSidebar();
  const { user, logout } = useAuth(); // Get user and logout from AuthContext
  const { openSearch } = useCommandSearch();
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Add keyboard shortcut listener for search
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        openSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openSearch]);

  const handleToggle = () => {
    if (window.innerWidth >= 1024) {
      toggleSidebar();
    } else {
      toggleMobileSidebar();
    }
  };


  return (
    <header className="relative flex w-full bg-white border-b border-gray-200 z-50 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center justify-between w-full px-3 py-2 lg:px-6 lg:py-4">
        {/* Left Section - Mobile and Desktop */}
        <div className="flex items-center gap-3">
          {/* Menu Toggle - Animated Arrow */}
          <button
            className="flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 lg:w-10 lg:h-10"
            onClick={handleToggle}
            aria-label="Toggle Sidebar"
          >
            <motion.svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              animate={{ 
                rotate: isMobile ? (isMobileOpen ? 0 : 180) : (isExpanded ? 0 : 180)
              }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
                stroke="currentColor"
                fill="none"
              />
            </motion.svg>
          </button>

          {/* Logo - Always visible on mobile (since removed from sidebar) */}
          <Link to="/" className="flex items-center lg:hidden">
            <img
              className="h-8 w-auto dark:hidden"
              src="/images/logo/guideme-logo.png"
              alt="GuideMe"
            />
            <img
              className="hidden h-8 w-auto dark:block"
              src="/images/logo/guideme-dark.png"
              alt="GuideMe"
            />
          </Link>

          {/* Desktop Search */}
          <div className="hidden lg:block flex-1 max-w-md">
            <CommandSearch />
          </div>
        </div>

        {/* Right Section - Quick Actions */}
        <div className="flex items-center gap-2">
          {/* Mobile Search Button */}
          <button
            onClick={() => {
              // Debug info removed for production
              openSearch();
            }}
            className="flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 lg:hidden"
            aria-label="Search"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>

          {/* Dark Mode Toggle - Hidden on mobile since it's now in FooterMobile */}
          <div className="hidden lg:block">
            <ThemeToggleButton />
          </div>

          {/* Email Verification Chip - Hidden on mobile */}
          <div className="hidden lg:block">
            <EmailVerificationChip />
          </div>

          {/* Notifications */}
          <NotificationDropdown />

          {/* Calendar Link - Desktop Only */}
          <Link 
            to="/calendar" 
            className="hidden md:flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            title={t('layout.myCalendar')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </Link>

          {/* User Area */}
          <UserDropdown user={user} logout={logout} />
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
